import { runOCR } from './file-ocr';
import { DEFAULT_NORMALIZE_LANG_HINT } from './constants';
import { logger } from '@/lib/logger';
import type {
    NormalizeResult,
    OcrAttemptResult,
    OCRAttemptStatus,
    OCRPreprocessProfile,
    OCRPromptProfile,
    PerformOcrResult,
} from './ocr-types';

interface OcrOptions {
    lang?: string;
    docId?: string;
    pageIndex?: number;
    retryBudget?: number;
    docTypeHint?: 'book' | 'form' | 'generic';
    /** Aborts the whole job. Cancellation is never retried; see fetchWithTimeout. */
    signal?: AbortSignal;
}

type GeminiRetryProfile = {
    promptProfile: OCRPromptProfile;
    preprocessProfile: OCRPreprocessProfile;
};

const GEMINI_RETRY_PROFILES: GeminiRetryProfile[] = [
    { promptProfile: 'extract_strict', preprocessProfile: 'standard' },
    { promptProfile: 'extract_strict', preprocessProfile: 'high_contrast' },
    { promptProfile: 'extract_sparse_layout', preprocessProfile: 'high_contrast_sharpen' },
];

const GEMINI_PDF_RETRY_PROFILES: GeminiRetryProfile[] = [
    { promptProfile: 'extract_strict', preprocessProfile: 'standard' },
    { promptProfile: 'extract_sparse_layout', preprocessProfile: 'standard' },
];

/**
 * Ceiling for a single /api/ocr call. The slowest successful request measured
 * against production was 36.3 s, so this leaves generous headroom: the point is
 * to bound a request that has genuinely stalled, not to cut short a slow one.
 * Applied per attempt, and a timeout ends the retry ladder (see performOCR), so
 * the worst case for a job is one timeout plus the local Tesseract fallback
 * rather than three timeouts in series.
 */
const OCR_REQUEST_TIMEOUT_MS = 90_000;

/**
 * Ceiling for the /api/normalize call.
 *
 * This was 60 s, on the reasoning that normalization is text-only and cosmetic.
 * Both halves of that turned out to be wrong for long documents.
 *
 * Not cosmetic: a 72 423-character document on 2026-08-24 came back with 709
 * line merges - 709 places where an OCR line-wrap was joined back into its
 * sentence. Raw output with those 709 breaks left in is exactly the complaint the
 * client's reviewers wrote up as text with no composition or order.
 *
 * And 60 s was under the real cost. That same request took 67.7 s server-side, so
 * the browser gave up at 60, degraded to raw text exactly as designed, and the
 * finished document arrived 7.7 s later with nobody left to receive it. The pass
 * had worked perfectly; the ceiling threw it away.
 *
 * 150 s applies the rule the OCR ceiling already uses - roughly 2.2x the slowest
 * measured success, against 2.5x there - and the platform is not the constraint:
 * a function ran to completion at 130 s the same day. A ceiling is not a target.
 * It only bites when something has genuinely stalled, and the alternative it was
 * choosing was to silently discard the formatting of every long document after
 * the user had already waited minutes for the OCR.
 *
 * The real fix for the wait is not a bigger number: it is to stop making the user
 * wait for this pass at all, by normalizing per page inside the OCR loop or by
 * showing raw text immediately and swapping when the pass returns. Both are real
 * changes to a route with page-break preservation logic, and neither is this one.
 */
const NORMALIZE_REQUEST_TIMEOUT_MS = 150_000;

/** The job was aborted deliberately - by the cancel button or a new job. */
export class OcrCancelledError extends Error {
    constructor(message = 'OCR request cancelled') {
        super(message);
        this.name = 'OcrCancelledError';
    }
}

/**
 * The device reported itself offline, so the request was never attempted.
 *
 * Typed because the caller has to act on it: this is the one failure where
 * continuing to the next page is pointless, and it used to be distinguishable
 * only by string-matching the message.
 */
export class OcrOfflineError extends Error {
    constructor(message = 'PDF Gemini OCR is only available in online mode') {
        super(message);
        this.name = 'OcrOfflineError';
    }
}

/** A single request exceeded its budget. */
export class OcrTimeoutError extends Error {
    readonly timeoutMs: number;

    constructor(timeoutMs: number) {
        super(`OCR request timed out after ${timeoutMs}ms`);
        this.name = 'OcrTimeoutError';
        this.timeoutMs = timeoutMs;
    }
}

/*
 * Both predicates test `name` rather than using instanceof. Subclass identity
 * does not survive every bundling and re-export path, and these errors cross a
 * module boundary into app/page.tsx; the name does survive.
 */
export function isOcrCancellation(error: unknown): boolean {
    return error instanceof Error && error.name === 'OcrCancelledError';
}

export function isOcrTimeout(error: unknown): boolean {
    return error instanceof Error && error.name === 'OcrTimeoutError';
}

export function isOcrOffline(error: unknown): boolean {
    return error instanceof Error && error.name === 'OcrOfflineError';
}

/**
 * True when Gemini read the image normally and returned no text at all.
 *
 * The distinction that matters is between a blank page and a failed request.
 * The route sends status 'empty' whenever the extracted text is zero-length,
 * and always sends a finishReason, defaulting it to 'STOP'. So 'empty' with a
 * normal stop means the model processed the image and found nothing; anything
 * else — a truncation, a safety stop, a thrown request — means we never really
 * got an answer, and the caller should keep its fallbacks.
 */
function isConfidentEmpty(attempt: OcrAttemptResult): boolean {
    return attempt.provider === 'gemini'
        && attempt.status === 'empty'
        && attempt.finishReason === 'STOP';
}

/**
 * fetch with a hard per-request timeout, cancellable from outside.
 *
 * Deliberately avoids AbortSignal.timeout() and AbortSignal.any(): JavaScriptCore
 * only gained them in Safari 16 and 17.4, and this app still serves users on
 * iOS 15. For the same reason a timeout is told apart from a cancellation by a
 * closure flag instead of the reason passed to abort() - Safari before 17
 * discards that argument.
 */
async function fetchWithTimeout(
    input: string,
    init: RequestInit,
    timeoutMs: number,
    externalSignal?: AbortSignal
): Promise<Response> {
    if (externalSignal?.aborted) {
        throw new OcrCancelledError();
    }

    const controller = new AbortController();
    let timedOut = false;

    const timer: ReturnType<typeof setTimeout> = setTimeout(() => {
        timedOut = true;
        controller.abort();
    }, timeoutMs);

    const forwardAbort = () => controller.abort();
    externalSignal?.addEventListener('abort', forwardAbort);

    try {
        return await fetch(input, { ...init, signal: controller.signal });
    } catch (error) {
        // Order matters: a cancellation arriving in the same tick as the timeout
        // should still read as a cancellation, so check the external signal first.
        if (externalSignal?.aborted) {
            throw new OcrCancelledError();
        }
        if (timedOut) {
            throw new OcrTimeoutError(timeoutMs);
        }
        throw error;
    } finally {
        clearTimeout(timer);
        externalSignal?.removeEventListener('abort', forwardAbort);
    }
}

/**
 * Performs OCR on the given file.
 * Uses Gemini retry ladder first, then falls back to Tesseract.js as last resort.
 * 
 * @param file - The image file to process
 * @param options - Optional configuration
 * @returns Promise with extracted text and execution metadata
 */
export async function performOCR(file: File, options?: OcrOptions): Promise<PerformOcrResult> {
    if (file.type === 'application/pdf') {
        return performGeminiOCRForPdf(file, options);
    }

    // Check if we're online
    const isOnline = typeof navigator !== 'undefined' && navigator.onLine;
    const attempts: OcrAttemptResult[] = [];
    const docTypeHint: 'book' | 'form' | 'generic' = options?.docTypeHint ?? 'generic';
    const retryBudget = Math.max(1, Math.min(options?.retryBudget ?? 3, GEMINI_RETRY_PROFILES.length));
    
    logger.debug('OCR request', { fileName: file.name, fileSize: file.size, isOnline, retryBudget });
    
    if (isOnline) {
        for (let i = 0; i < retryBudget; i++) {
            const profile = GEMINI_RETRY_PROFILES[i];
            const attemptNumber = i + 1;

            if (options?.signal?.aborted) {
                throw new OcrCancelledError();
            }

            try {
                const attemptResult = await performGeminiOCR(file, {
                    promptProfile: profile.promptProfile,
                    preprocessProfile: profile.preprocessProfile,
                    attempt: attemptNumber,
                    docId: options?.docId,
                    pageIndex: options?.pageIndex,
                    docTypeHint,
                    signal: options?.signal,
                });
                attempts.push(attemptResult);

                logger.info('Gemini OCR attempt completed', {
                    pageIndex: options?.pageIndex,
                    attempt: attemptNumber,
                    status: attemptResult.status,
                    textLength: attemptResult.textLength,
                    promptProfile: profile.promptProfile,
                    preprocessProfile: profile.preprocessProfile,
                });

                if (attemptResult.status === 'ok' && attemptResult.text.trim().length > 0) {
                    return {
                        text: attemptResult.text,
                        rawText: attemptResult.text,
                        provider: 'gemini',
                        status: 'ok',
                        blocked: false,
                        attempts,
                        usedFallback: false,
                    };
                }
            } catch (error) {
                // A cancelled job is not a failed attempt: propagate it untouched so
                // the caller does not fall through to Tesseract or record a result.
                if (isOcrCancellation(error)) {
                    throw error;
                }

                const errorMsg = error instanceof Error ? error.message : 'Unknown error';
                attempts.push({
                    provider: 'gemini',
                    attempt: attemptNumber,
                    status: 'error',
                    text: '',
                    textLength: 0,
                    blocked: false,
                    error: errorMsg,
                    promptProfile: profile.promptProfile,
                    preprocessProfile: profile.preprocessProfile,
                });

                logger.warn('Gemini OCR attempt failed', {
                    pageIndex: options?.pageIndex,
                    attempt: attemptNumber,
                    error: errorMsg,
                });

                // One attempt already exceeded a 90 s budget. Two more would cost
                // the user another three minutes for the same likely outcome, so
                // give up on Gemini and let Tesseract answer locally instead.
                if (isOcrTimeout(error)) {
                    logger.warn('Gemini OCR timed out, ending retry ladder early', {
                        pageIndex: options?.pageIndex,
                        attempt: attemptNumber,
                        remainingAttempts: retryBudget - attemptNumber,
                    });
                    break;
                }
            }
        }
    } else {
        logger.info('Offline mode, skipping Gemini retry ladder', { pageIndex: options?.pageIndex });
    }
    
    /*
     * A page Gemini read and found no text in is blank, not failed. Running
     * Tesseract over it is the single most expensive step in the whole ladder —
     * in-browser OCR on a phone — to re-derive an answer we already have, and on
     * a blank input the best case is that it also returns nothing while the
     * worst case is that it returns noise we then present as the document.
     *
     * Only skipped when EVERY attempt came back a clean empty. One error in the
     * set means the ladder never actually got an answer, and there the fallback
     * still earns its keep. Offline runs reach here with no Gemini attempts at
     * all, so they keep falling through, which is the whole point of that path.
     */
    const geminiAttempts = attempts.filter((attempt) => attempt.provider === 'gemini');

    /*
     * Every rung hit the output cap, and at least one came back with text.
     *
     * Tesseract is the wrong answer here, and this is the one place the usual
     * "fall back on failure" reasoning inverts. Truncation is not the model
     * failing to read the page - it read it, and ran out of room saying so. The
     * text it did return is accurate up to where it stops, which is more than
     * in-browser Tesseract on a Polish phone photo is likely to manage across the
     * whole page. So the longest partial answer wins, and it is labelled
     * `gemini_truncated` rather than `gemini_ok` so that nothing downstream can
     * mistake a half page for a whole one. DYS-038.
     */
    const truncatedWithText = geminiAttempts
        .filter((attempt) => attempt.status === 'truncated' && attempt.text.trim().length > 0)
        .sort((a, b) => b.text.length - a.text.length)[0];

    if (truncatedWithText && geminiAttempts.every((attempt) => attempt.status === 'truncated')) {
        logger.warn('Every Gemini attempt hit the output cap, keeping the longest partial page', {
            pageIndex: options?.pageIndex,
            attempts: geminiAttempts.length,
            textLength: truncatedWithText.textLength,
        });

        return {
            text: truncatedWithText.text,
            rawText: truncatedWithText.text,
            provider: 'gemini',
            status: 'truncated',
            blocked: false,
            attempts,
            usedFallback: false,
        };
    }

    if (geminiAttempts.length > 0 && geminiAttempts.every(isConfidentEmpty)) {
        logger.info('Gemini read the page and found no text, skipping Tesseract', {
            pageIndex: options?.pageIndex,
            attempts: geminiAttempts.length,
        });

        return {
            text: '',
            rawText: '',
            provider: 'gemini',
            status: 'empty',
            blocked: false,
            attempts,
            usedFallback: false,
        };
    }

    // Offline or Gemini retry ladder exhausted - use Tesseract as last-resort fallback
    logger.warn('Gemini retry ladder exhausted, falling back to Tesseract', {
        pageIndex: options?.pageIndex,
        attempts: attempts.length,
    });

    if (options?.signal?.aborted) {
        throw new OcrCancelledError();
    }

    const tesseractResult = await performTesseractOCR(file, options?.lang);
    const tesseractAttempt: OcrAttemptResult = {
        provider: 'tesseract',
        attempt: attempts.length + 1,
        status: tesseractResult.text.trim().length > 0 ? 'ok' : 'empty',
        text: tesseractResult.text,
        textLength: tesseractResult.text.length,
        blocked: false,
        durationMs: tesseractResult.durationMs,
    };
    attempts.push(tesseractAttempt);

    return {
        text: tesseractResult.text,
        rawText: tesseractResult.text,
        provider: 'tesseract',
        status: tesseractAttempt.status,
        blocked: false,
        attempts,
        usedFallback: true,
    };
}

/**
 * Performs OCR on a PDF file using Gemini only (no Tesseract fallback).
 * Intended for direct PDF-to-Gemini flow where we don't want to run Tesseract
 * on non-image inputs. Returns the same shape as performOCR for compatibility.
 */
export async function performGeminiOCRForPdf(file: File, options?: OcrOptions): Promise<PerformOcrResult> {
    const isOnline = typeof navigator !== 'undefined' && navigator.onLine;
    const attempts: OcrAttemptResult[] = [];
    const docTypeHint: 'book' | 'form' | 'generic' = options?.docTypeHint ?? 'generic';
    const retryBudget = Math.max(1, Math.min(options?.retryBudget ?? 3, GEMINI_PDF_RETRY_PROFILES.length));

    logger.debug('PDF Gemini-only OCR request', {
        fileName: file.name,
        fileSize: file.size,
        isOnline,
        retryBudget,
    });

    if (!isOnline) {
        // For PDFs, we rely on a separate offline pipeline (PDF→images→Tesseract),
        // which is not wired up on this path. Typed so the multi-page caller can
        // stop instead of asking the same question once per remaining page.
        throw new OcrOfflineError();
    }

    // There is no Tesseract fallback on this path, so a timeout has to surface to
    // the caller rather than being flattened into an empty result - "took too long"
    // and "no text found" call for different messages and different user action.
    let timeoutError: OcrTimeoutError | null = null;

    for (let i = 0; i < retryBudget; i++) {
        const profile = GEMINI_PDF_RETRY_PROFILES[i];
        const attemptNumber = i + 1;

        if (options?.signal?.aborted) {
            throw new OcrCancelledError();
        }

        try {
            const attemptResult = await performGeminiOCR(file, {
                promptProfile: profile.promptProfile,
                preprocessProfile: profile.preprocessProfile,
                attempt: attemptNumber,
                docId: options?.docId,
                pageIndex: options?.pageIndex,
                docTypeHint,
                signal: options?.signal,
            });
            attempts.push(attemptResult);

            logger.info('PDF Gemini OCR attempt completed', {
                pageIndex: options?.pageIndex,
                attempt: attemptNumber,
                status: attemptResult.status,
                textLength: attemptResult.textLength,
                promptProfile: profile.promptProfile,
                preprocessProfile: profile.preprocessProfile,
            });

            if (attemptResult.status === 'ok' && attemptResult.text.trim().length > 0) {
                return {
                    text: attemptResult.text,
                    rawText: attemptResult.text,
                    provider: 'gemini',
                    status: 'ok',
                    blocked: attemptResult.blocked,
                    blockReason: attemptResult.blockReason,
                    attempts,
                    usedFallback: false,
                };
            }
        } catch (error) {
            if (isOcrCancellation(error)) {
                throw error;
            }

            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            attempts.push({
                provider: 'gemini',
                attempt: attemptNumber,
                status: 'error',
                text: '',
                textLength: 0,
                blocked: false,
                error: errorMsg,
                promptProfile: profile.promptProfile,
                preprocessProfile: profile.preprocessProfile,
            });

            logger.warn('PDF Gemini OCR attempt failed', {
                pageIndex: options?.pageIndex,
                attempt: attemptNumber,
                error: errorMsg,
            });

            if (isOcrTimeout(error)) {
                timeoutError = error as OcrTimeoutError;
                logger.warn('PDF Gemini OCR timed out, ending retry ladder early', {
                    pageIndex: options?.pageIndex,
                    attempt: attemptNumber,
                    remainingAttempts: retryBudget - attemptNumber,
                });
                break;
            }
        }
    }

    if (timeoutError) {
        throw timeoutError;
    }

    // All Gemini attempts failed or returned empty text
    const lastAttempt = attempts[attempts.length - 1];
    const status = lastAttempt?.status || 'empty';

    return {
        text: '',
        rawText: '',
        provider: 'gemini',
        status,
        blocked: lastAttempt?.blocked ?? false,
        blockReason: lastAttempt?.blockReason,
        attempts,
        usedFallback: false,
    };
}

/**
 * Calls the /api/ocr endpoint which uses Gemini extraction
 */
async function performGeminiOCR(
    file: File,
    options: {
        promptProfile: OCRPromptProfile;
        preprocessProfile: OCRPreprocessProfile;
        attempt: number;
        docId?: string;
        pageIndex?: number;
        docTypeHint?: 'book' | 'form' | 'generic';
        signal?: AbortSignal;
    }
): Promise<OcrAttemptResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('promptProfile', options.promptProfile);
    formData.append('preprocessProfile', options.preprocessProfile);
    formData.append('attempt', String(options.attempt));
    if (options.docId) {
        formData.append('docId', options.docId);
    }
    if (typeof options.pageIndex === 'number') {
        formData.append('pageIndex', String(options.pageIndex));
    }
    if (options.docTypeHint) {
        formData.append('docTypeHint', options.docTypeHint);
    }
    
    logger.debug('Calling Gemini API', {
        fileSize: file.size,
        pageIndex: options.pageIndex,
        attempt: options.attempt,
        promptProfile: options.promptProfile,
        preprocessProfile: options.preprocessProfile,
    });
    
    const response = await fetchWithTimeout(
        '/api/ocr',
        { method: 'POST', body: formData },
        OCR_REQUEST_TIMEOUT_MS,
        options.signal
    );
    
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.error('Gemini API error', { status: response.status, error: errorData.error });
        throw new Error(errorData.error || `HTTP error ${response.status}`);
    }
    
    const data = await response.json() as {
        text?: string;
        blocked?: boolean;
        blockReason?: string;
        status?: OCRAttemptStatus;
        durationMs?: number;
        finishReason?: string;
        promptProfile?: OCRPromptProfile;
        preprocessProfile?: OCRPreprocessProfile;
    };
    const text = data.text || '';
    const status = data.status || (text.trim().length > 0 ? 'ok' : 'empty');
    
    return {
        provider: 'gemini',
        attempt: options.attempt,
        status,
        text,
        textLength: text.length,
        blocked: data.blocked || status === 'blocked',
        blockReason: data.blockReason,
        durationMs: data.durationMs,
        finishReason: data.finishReason,
        promptProfile: data.promptProfile || options.promptProfile,
        preprocessProfile: data.preprocessProfile || options.preprocessProfile,
    };
}

/** The unchanged input, shaped as a NormalizeResult. */
function rawTextResult(rawText: string, usedRawFallback: boolean): NormalizeResult {
    return {
        normalizedText: rawText,
        stats: {
            inputLength: rawText.length,
            outputLength: rawText.length,
            driftRatio: 0,
            lineMerges: 0,
        },
        usedRawFallback,
        rejectReason: null,
    };
}

/**
 * Normalizes extracted OCR text using dedicated AI endpoint.
 * Falls back to raw text if normalization is rejected by server guard rails.
 *
 * The language hint defaults here rather than at the call sites. It was optional
 * and all three callers omitted it, which silently disabled the route's Polish
 * rules for the entire life of the feature — the kind of omission that leaves no
 * trace and that a fourth caller would repeat. Callers can still override it.
 */
export async function normalizeOCRText(
    rawText: string,
    options?: {
        langHint?: string;
        docTypeHint?: 'book' | 'form' | 'generic';
        pageIndex?: number;
        signal?: AbortSignal;
    }
): Promise<NormalizeResult> {
    const langHint = options?.langHint ?? DEFAULT_NORMALIZE_LANG_HINT;

    if (!rawText || rawText.trim().length === 0) {
        return rawTextResult(rawText, false);
    }

    let response: Response;
    try {
        response = await fetchWithTimeout(
            '/api/normalize',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    rawText,
                    langHint,
                    docTypeHint: options?.docTypeHint || 'generic',
                }),
            },
            NORMALIZE_REQUEST_TIMEOUT_MS,
            options?.signal
        );
    } catch (error) {
        // Normalization is cosmetic and it runs *after* the expensive OCR pass.
        // Throwing away a document the user already waited 35 s for, because the
        // tidy-up stalled, is the worse outcome - so a timeout degrades to raw
        // text, exactly as a server-side rejection already does. Cancellations
        // and genuine network errors keep their existing behaviour.
        if (isOcrTimeout(error)) {
            logger.warn('OCR normalization timed out, using raw text', {
                pageIndex: options?.pageIndex,
                timeoutMs: NORMALIZE_REQUEST_TIMEOUT_MS,
            });
            return rawTextResult(rawText, true);
        }
        throw error;
    }

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData?.error || `HTTP error ${response.status}`;
        logger.warn('OCR normalization failed, using raw text', {
            pageIndex: options?.pageIndex,
            error: errorMessage,
        });
        return rawTextResult(rawText, true);
    }

    const data = await response.json() as NormalizeResult;
    return {
        normalizedText: data.normalizedText || rawText,
        stats: data.stats,
        usedRawFallback: data.usedRawFallback || false,
        rejectReason: data.rejectReason ?? null,
    };
}

/**
 * Uses local Tesseract.js for OCR (last-resort fallback).
 */
async function performTesseractOCR(file: File, lang?: string): Promise<{ text: string; durationMs: number }> {
    const startTime = Date.now();
    logger.info('Starting Tesseract OCR', { lang: lang || 'pol' });
    
    const text = await runOCR(file, 0, lang || 'pol');
    const durationMs = Date.now() - startTime;
    
    logger.info('Tesseract OCR completed', { duration: durationMs, textLength: text.length });
    
    return {
        text,
        durationMs,
    };
}

/**
 * Simple wrapper that returns just the text (for backward compatibility)
 */
export async function extractText(file: File, options?: OcrOptions): Promise<string> {
    const result = await performOCR(file, options);
    return result.text;
}
